import { apiClient, toCamelCase, toSnakeCase } from "./client";
import type {
  FileRecord,
  FileListParams,
  FileListResponse,
  FileUploadMetadata,
  FolderNode,
  TagCount
} from "@/types/file";

// Use the same base URL as apiClient for consistency.
// NEXT_PUBLIC_API_URL puede traer o no el sufijo `/api/v1`; no lo duplicamos.
const BASE_URL = process.env.NEXT_PUBLIC_API_URL || "";
const API_URL = BASE_URL
  ? (BASE_URL.replace(/\/$/, "").endsWith("/api/v1")
      ? BASE_URL.replace(/\/$/, "")
      : `${BASE_URL.replace(/\/$/, "")}/api/v1`)
  : "/api/v1";

export const filesApi = {
  /**
   * Upload a general file
   */
  upload: async (file: File, folder?: string): Promise<FileRecord> => {
    const formData = new FormData();
    formData.append("file", file);

    const url = folder ? `/files/upload?folder=${encodeURIComponent(folder)}` : "/files/upload";

    const response = await apiClient.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return toCamelCase(response.data) as FileRecord;
  },

  /**
   * Get file info by ID
   */
  getById: async (fileId: number): Promise<FileRecord> => {
    const response = await apiClient.get(`/files/${fileId}`);
    return toCamelCase(response.data) as FileRecord;
  },

  /**
   * Get file download URL (unauthenticated - use downloadFile for auth)
   */
  getDownloadUrl: (fileId: number): string => {
    return `${API_URL}/files/${fileId}/download`;
  },

  /**
   * Download a file with authentication (returns blob)
   */
  downloadFile: async (fileId: number): Promise<Blob> => {
    const response = await apiClient.get(`/files/${fileId}/download`, {
      responseType: "blob",
    });
    return response.data;
  },

  /**
   * Delete a file
   */
  delete: async (fileId: number): Promise<void> => {
    await apiClient.delete(`/files/${fileId}`);
  },

  /**
   * Mejora un documento generado con IA según una instrucción en lenguaje
   * natural. Devuelve el nuevo File (versión); el original se conserva.
   */
  improve: async (fileId: number, instruction: string): Promise<FileRecord> => {
    const response = await apiClient.post(`/files/${fileId}/improve`, {
      instruction,
    });
    return toCamelCase(response.data) as FileRecord;
  },

  // NEW DOCUMENT MANAGEMENT METHODS

  /**
   * Get list of files with filters and pagination
   */
  getAll: async (params?: FileListParams): Promise<FileListResponse> => {
    const response = await apiClient.get("/files", {
      params: toSnakeCase(params),
    });
    return (toCamelCase(response.data) as FileListResponse) || { items: [], total: 0 };
  },

  /**
   * Upload file with full metadata
   */
  uploadWithMetadata: async (
    file: File,
    metadata: FileUploadMetadata
  ): Promise<FileRecord> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("document_type", metadata.documentType);

    if (metadata.spaceId) {
      formData.append("space_id", metadata.spaceId.toString());
    }
    if (metadata.role) {
      formData.append("role", metadata.role);
    }
    if (metadata.tags && metadata.tags.length > 0) {
      formData.append("tags", JSON.stringify(metadata.tags));
    }
    if (metadata.description) {
      formData.append("description", metadata.description);
    }
    if (metadata.isPublic !== undefined) {
      formData.append("is_public", metadata.isPublic.toString());
    }

    const response = await apiClient.post(
      "/files/upload-with-metadata",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return toCamelCase(response.data) as FileRecord;
  },

  /**
   * Get folder structure for navigation
   */
  getFolderStructure: async (): Promise<FolderNode[]> => {
    const response = await apiClient.get("/files/folders");
    return (toCamelCase(response.data) as FolderNode[]) || [];
  },

  /**
   * Get all tags with counts
   */
  getTags: async (): Promise<TagCount[]> => {
    const response = await apiClient.get("/files/tags");
    return (toCamelCase(response.data) as TagCount[]) || [];
  },

  /**
   * Update file metadata (PATCH semántico).
   * Solo se aplican los campos enviados. `slotAssignments` reemplaza la lista
   * completa.
   */
  updateMetadata: async (
    fileId: number,
    metadata: {
      role?: "reference" | "source" | "generated" | "attachment";
      documentType?: string;
      tags?: string[];
      description?: string | null;
      folderPath?: string | null;
      isPublic?: boolean;
      slotAssignments?: Array<{ caseId: number; slotKey: string }>;
    }
  ): Promise<FileRecord> => {
    const response = await apiClient.patch(
      `/files/${fileId}`,
      toSnakeCase(metadata)
    );
    return toCamelCase(response.data) as FileRecord;
  },

  /**
   * Pide al backend que el LLM mire el contenido del fichero y sugiera a qué
   * input_slot del caso pertenece. Si confidence="low" o slot_key=null,
   * el cliente debería ignorar la sugerencia.
   */
  classifyToSlot: async (
    fileId: number,
    caseId: number
  ): Promise<{
    slotKey: string | null;
    confidence: "high" | "medium" | "low";
    reasoning: string;
    source: "llm" | "no_content" | "no_slots" | "error";
  }> => {
    const response = await apiClient.post(
      `/files/${fileId}/classify-slot`,
      undefined,
      { params: { case_id: caseId } }
    );
    return toCamelCase(response.data) as {
      slotKey: string | null;
      confidence: "high" | "medium" | "low";
      reasoning: string;
      source: "llm" | "no_content" | "no_slots" | "error";
    };
  },

  /**
   * Move file to new location
   */
  moveFile: async (
    fileId: number,
    params: {
      newFolderPath?: string;
    }
  ): Promise<FileRecord> => {
    const response = await apiClient.post(
      `/files/${fileId}/move`,
      toSnakeCase(params)
    );
    return toCamelCase(response.data) as FileRecord;
  },
};
